import { Injectable } from '@angular/core';
import { CommonUtilityService } from 'src/app/shared/utility-services/common-utility.service';
import { FormConfig, FormNavigationConfig, DefaultFormConfig, PageConfigInterface } from '@ptl/forms';
import { environment } from 'src/environments/environment';
import { ZipCountyStateStructure } from '../../../shared/interfaces/zipCountyStateStructure.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { PlansStateStructure } from '../../../plan-selection/configs/interfaces/plansStateStructure.interface';
import { PlanUtilityService } from '../../../plan-selection/plan-summary-list/plan-utility.service';
import { HTTPWrapperContext, RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES } from '@ptl/xhr';
import { HttpHeaders } from '@angular/common/http';
import { ContentStorageService } from '@ptl/global-components';

@Injectable({
  providedIn: 'root'
})
export class FormConfigUtilityService {

  DEFAULT_METATAGS = [];

  formConfigStrategy: Map<string, any>;
  metatagPriorityByIndex: string[];
  metaTagValueOverride: any = null;
  jsonMap: any;
  formCache = {};

  constructor(
    private commonUtilityService: CommonUtilityService,
    private reducerUtilityService: ReducerUtilityService,
    private planUtilityService: PlanUtilityService,
    private contentStorage: ContentStorageService,
    private httpClient: HTTPWrapperContext
  ) { }

  async getFormConfigFromMap(formConfigStrategy: string): Promise<DefaultFormConfig> {
    // Load all the form files in 3 distinct steps - Forms Map -> Specific Form Config -> Pages for that form
    const formConfigJson = await this.getJsonFormConfig(formConfigStrategy);
    if (formConfigJson.url) {
      const formConfigJson = await this.getJsonFileFromService(this.jsonMap[0].url);

      // Create a deep copy of the form config object to not pollute the cache, then fetch all the pages needed for the overall form
      const formConfig: DefaultFormConfig = JSON.parse(JSON.stringify(formConfigJson.formConfiguration));
      for (let i = 0; i < formConfig.pages.length; i++) {
        const page = await this.getJsonFileFromService(formConfig.pages[i]);
        formConfig.pages[i] = page as unknown as PageConfigInterface;
      }

      return formConfig;
    }
  };

  async getFormConfigFromMapAndMetatags(formConfigStrategy: string, metaTagValueOverride: any) {
    // force a specific set of meta tag values to use instead of using the state. Important for the mass upload grid
    this.metaTagValueOverride = metaTagValueOverride;

    return this.getFormConfigFromMap(formConfigStrategy);
  }

  async getJsonFormConfig(formConfigStrategy: string): Promise<any> {
    // Assert we have a content storage component map for whatever strategy is being passed in
    this.formConfigStrategy = this.contentStorage.getContentMap(formConfigStrategy);
    if (this.formConfigStrategy.size === 0) {
      console.error('No content storage component found for config key ' + formConfigStrategy);
      return {};
    }

    // First we need to load the forms map.
    // The forms map contains the filters to choose the specific form config based on region, asp, etc.
    const requestUrl = this.formConfigStrategy.get('url');
    this.jsonMap = await this.getJsonFileFromService(requestUrl);

    // This function assigns the correct form config to the class member jsonMap based on which configuration matches our current meta tags
    this.chooseFormJsonByMetatagPriority();

    if (this.jsonMap[0]) {
      return this.jsonMap[0];
    }
    else {
      console.error('No form map found for config key ' + formConfigStrategy);
      return {};
    }
  }

  chooseFormJsonByMetatagPriority() {
    // Get the metatags priority for the form type.
    this.metatagPriorityByIndex = this.getFormMetatagPriorityByIndex();
    let metatagComparators = this.getMetaTagComparators();

    // First filter out non matches
    for (const tag of this.metatagPriorityByIndex) {
      if (this.jsonMap.length > 1) {
        this.jsonMap = this.jsonMap.filter((config) => {
          let filterCheck = true;
          if (metatagComparators[tag] && config.meta[tag]) {
            filterCheck = metatagComparators[tag] === config.meta[tag];
          }
          return filterCheck;
        })
      } else {
        break;
      }
    }
    
    // Second determine metatag priority by sorting on matching tags
    this.jsonMap.sort((configA, configB) => {
      for (const tag of this.metatagPriorityByIndex) {
        // Only configA tag matches so it has priority
        if (metatagComparators[tag] === configA.meta[tag] && metatagComparators[tag] !== configB.meta[tag]) {
          return -1;
        }
        
        // Only configB tag matches so it has priority
        if (metatagComparators[tag] === configB.meta[tag] && metatagComparators[tag] !== configA.meta[tag]) {
          return 1;
        }
      }
        
      // Neither config matches any tags
      return 0;
    });
  }

  getFormMetatagPriorityByIndex(): string[] {
    const metaTagPriorityByIndex = this.formConfigStrategy.get('metaTagPriorityByIndex');

    if (metaTagPriorityByIndex) {
      return Array.isArray(metaTagPriorityByIndex) ? metaTagPriorityByIndex : JSON.parse(metaTagPriorityByIndex);
    }
    else {
      return this.DEFAULT_METATAGS;
    }
  }

  getMetaTagComparators() {
    if (this.metaTagValueOverride) {
      return this.metaTagValueOverride;
    }
    
    const zipcountyState: ZipCountyStateStructure = this.reducerUtilityService.getState('ZipCounty');
    const planState: PlansStateStructure = this.reducerUtilityService.getState('Plans');

    const metatags: any = {};
    metatags.asp = environment.initData.brand.ASPID;
    metatags.productGroup = planState.activeProductGroup;
    metatags.state = zipcountyState.county?.state;
    metatags.region = zipcountyState.county?.region;
    metatags.serviceZones = zipcountyState.county?.serviceZones;
    metatags.county = zipcountyState.county?.county;
    metatags.region = zipcountyState.county?.region;
    metatags.product = this.planUtilityService.getSelectedPlan()?.attributes?.SUB_PRODUCT_TYPE; // Incomplete Logic here for Product.
    metatags.planName = this.planUtilityService.getSelectedPlan()?.productName;
    metatags.userType = this.commonUtilityService.getEnrollmentFormType();
    metatags.isPlanChange = planState.isPlanChange;

    return metatags;
  }

  async getJsonFileFromService(url): Promise<any> {
    if (this.formCache[url]) {
      return this.formCache[url];
    }

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: url,
      headers: new HttpHeaders({})
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.MOCK);
    this.formCache[url] = await this.httpClient.makeRequest(requestConfig);

    return this.formCache[url] as any;
  }
}
